import { type Conversation } from "shared/apiClient";
import { AssessmentLights } from "shared/types";
import { getText, interpolate } from "shared/lib";

import { useCouncilLocalisation } from "shared/hooks";

import { ExternalLinkIcon } from "shared/components";

export interface BAYProps {
  assessmentLights: string;
  conversation: Conversation | undefined | null;
}

export function BAY({ assessmentLights, conversation }: BAYProps) {
  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  const renderCheckForExistingPlanningPermits = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Check Council's register of existing planning permits")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "If your property has an existing permit, it is likely that an amendment will be required.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.bayside.vic.gov.au/services/planning-and-building/planning-application-status"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Check Council register")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderRequestCertificateOfTitle = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Request a Certificate of Title")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "This tool doesn't account for covenants (restrictions), caveats and easements, which can impact what you can and can't do on a property. A Certificate of Title can include this information, which you can request online.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href={getLocalisationEntry("councilApplyForCertificateOfTitleLink")}
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Request a Certificate of Title")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderCheckForOtherRequiredPermits = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Check for other required permits")}
        </h3>
        <p className="text-bodymd mb-2">
          {interpolate(
            "You may need other permits for your proposal, such as {{buildingPermitsLink}}, {{localLawPermitsLink}}, and {{assetProtectionPermitsLink}}.",
            {
              buildingPermitsLink: (
                <a
                  className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
                  href="https://www.bayside.vic.gov.au/services/planning-and-building/what-building-approval-do-i-need"
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("building permits")}
                </a>
              ),
              localLawPermitsLink: (
                <a
                  className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
                  href="https://www.bayside.vic.gov.au/services/trees-parks-and-beaches/apply-local-law-tree-pruning-or-removal-permit"
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("local law tree pruning or removal permits")}
                </a>
              ),
              assetProtectionPermitsLink: (
                <a
                  className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
                  href="https://www.bayside.vic.gov.au/services/planning-and-building/do-i-need-asset-protection-permit-or-any-other-building-construction-related-permits"
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("asset protection permits")}
                </a>
              ),
            },
          )}
        </p>
      </div>
    );
  };

  const renderApplyForWrittenPlanningAdvice = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Apply for written planning advice")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "If you need formal advice reviewed by Council's planners for your building permit, you may wish to apply for written planning advice.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.bayside.vic.gov.au/services/planning-and-building/request-planning-advice"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Apply for written planning advice")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderBookPreApplicationMeeting = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Book a pre-application meeting")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Discuss your project's application requirements, design compliance and the approvals process with one of Council's planners.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.bayside.vic.gov.au/services/planning-and-building/discuss-your-pre-application-project-planner"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Book a pre-application meeting")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderApplyForPermit = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Apply for a planning permit")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "If certain of your next steps, you may now apply via Council's online planning portal.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.bayside-vic.altitudelg.com/e-services/das/online/Init.do?nodeNum=477944"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Online planning portal")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderDiscussWithPlanners = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Discuss your proposal with a planner")}
        </h3>
        <p className="text-bodymd mb-2">
          {interpolate(
            "Have questions? {{link}} to speak to one of our planners. When you contact us, tell us your session ID:",
            {
              link: (
                <a
                  className="text-textLink hover:text-textLinkHover cursor-pointer underline"
                  href={getLocalisationEntry("councilContactLink")}
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("Contact us")}
                </a>
              ),
            },
          )}
        </p>
        <div className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodylg w-min text-nowrap rounded border px-2 py-1.5 font-medium">
          {conversation?.identifier?.toUpperCase() || ""}
        </div>
      </div>
    );
  };

  switch (assessmentLights) {
    case AssessmentLights.NoPermitRequired:
      return (
        <>
          {renderCheckForExistingPlanningPermits()}
          {renderCheckForOtherRequiredPermits()}
          {renderApplyForWrittenPlanningAdvice()}
          {renderRequestCertificateOfTitle()}
        </>
      );
    case AssessmentLights.MoreInformationRequired:
      return (
        <>
          {renderBookPreApplicationMeeting()}
          {renderApplyForWrittenPlanningAdvice()}
          {renderRequestCertificateOfTitle()}
          {renderDiscussWithPlanners()}
        </>
      );
    case AssessmentLights.RequiresPermit:
      return (
        <>
          {renderBookPreApplicationMeeting()}
          {renderApplyForWrittenPlanningAdvice()}
          {renderApplyForPermit()}
          {renderRequestCertificateOfTitle()}
        </>
      );
    case AssessmentLights.Prohibited:
      return <>{renderDiscussWithPlanners()}</>;
  }
}
